"use client";
import { HTMLAttributes, useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { signIn } from "next-auth/react";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/custom/button";
import { PasswordInput } from "@/components/custom/password-input";
import { cn } from "@/lib/utils";
import Link from "next/link";
import "react-toastify/dist/ReactToastify.css";
import { useRouter } from "next/navigation";
import api from "@/app/api";
import { AxiosError } from "axios";
import { toast } from "sonner";
import useAuthStore from "@/lib/useAuthStore";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { AlertCircle } from "lucide-react";

interface UserAuthFormProps extends HTMLAttributes<HTMLDivElement> {
  impersonationEmail?: string | null;
  impersonationCode?: string | null;
  email?: string;
  onEmailChange?: (email: string) => void;
}

const formSchema = z.object({
  email: z
    .string()
    .min(1, { message: "Please Enter Your Email" })
    .email({ message: "Invalid Email Address" }),
  password: z
    .string()
    .min(1, {
      message: "Please Enter Your Password",
    })
    .min(6, {
      message: "Password Must Be At Least 6 Characters Long",
    }),
});

export function UserAuthForm({
  className,
  impersonationEmail,
  impersonationCode,
  email,
  onEmailChange,
  ...props
}: UserAuthFormProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { loginUser } = useAuthStore();
  const router = useRouter();

  const isImpersonationLogin = !!(impersonationEmail && impersonationCode);

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      email: impersonationEmail
        ? decodeURIComponent(impersonationEmail)
        : email || "",
      password: "",
    },
  });

  // Keep email in sync when switching from OTP tab
  useEffect(() => {
    if (!isImpersonationLogin && typeof email === "string") {
      form.setValue("email", email);
    }
  }, [email, form, isImpersonationLogin]);

  // Handle impersonation login automatically when component mounts
  useEffect(() => {
    if (isImpersonationLogin) {
      handleImpersonationLogin();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isImpersonationLogin, impersonationEmail, impersonationCode]);

  // Function to handle impersonation login
  async function handleImpersonationLogin() {
    if (!impersonationEmail || !impersonationCode) return;

    setIsLoading(true);
    setError(null);

    try {
      // Call the impersonation verify API
      const response: any = await api.post('/auth/impersonate/verify', {
        email: decodeURIComponent(impersonationEmail),
        code: impersonationCode
      } , {withCredentials:true});

      if (response.data?.success && response.data?.access_token) {
        // Store the access token (same as normal login)
        localStorage.setItem('access_token', response.data.access_token);


        // Update auth store with user data
        const { loginUser } = useAuthStore.getState();

        // Create a mock login response to reuse existing login flow
        const mockLoginResponse = {
          data: {
            access_token: response.data.access_token,
            expires_in: response.data.expires_in,
            password_changed: response.data.password_changed,
            user: response.data.user
          }
        };

        // Update auth store state manually since we're not using the loginUser function
        useAuthStore.setState({
          user: response.data.user,
          isAuthenticated: true,
          isLoading: false,
          error: null,
          passwordChanged: response.data.password_changed
        });

        toast.success('Impersonation login successful');

        // Check if user needs to change password
        if (response.data.password_changed === false) {
          setTimeout(() => {
            router.push('/change-password');
          }, 100);
        } else {
          // Redirect to flight page
          setTimeout(() => {
            router.push('/flight');
          }, 100);
        }
      } else {
        router.push('/login')
        throw new Error('Invalid impersonation response');
      }
    } catch (error: any) {
      const errorMessage = error instanceof AxiosError
        ? error.response?.data?.message || "Impersonation login failed"
        : "An unexpected error occurred during impersonation login";

      setError(errorMessage);
      toast.error(errorMessage);
      router.push('/login')
    } finally {
      setIsLoading(false);
    }
  }

  async function onSubmit(data: z.infer<typeof formSchema>) {
    setIsLoading(true);
    setError(null);

    try {
      const response = await loginUser(data);

      // Check if user needs to change password
      if (response?.data?.password_changed === false) {
        // Use setTimeout to ensure all state updates complete before navigation
        setTimeout(() => {
          router.push('/change-password');
        }, 100);
      } else {
        // Redirect to flight page or saved redirect path with timeout
        setTimeout(() => {
          router.push('/flight');
        }, 100);
      }

      toast.success('Login Successful');
    } catch (error: any) {
      const errorMessage = error instanceof AxiosError
        ? error.response?.data?.message || "Login Failed"
        : "An Unexpected Error Occurred";

      setError(errorMessage);
      toast.error(errorMessage || 'Login Failed');
    } finally {
      setIsLoading(false);
    }
  }

  return (
    <div className={cn("grid gap-6", className)} {...props}>
      {error && (
        <Alert variant="destructive" className="flex items-center gap-2 mt-3">
          <span><AlertCircle className="h-4 w-4" /></span>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {isImpersonationLogin ? (
        // Show loading state for impersonation login
        <div className="flex flex-col items-center justify-center py-8 space-y-4">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#022f5d]"></div>
          <p className="text-sm text-muted-foreground">
            Verifying impersonation request...
          </p>
          <p className="text-xs text-muted-foreground">
            Email: {impersonationEmail ? decodeURIComponent(impersonationEmail) : ''}
          </p>
        </div>
      ) : (
        // Show normal login form
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)}>
            <div className="grid gap-2">
              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem className="space-y-1">
                    <FormLabel>Email</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="Enter Email Address"
                        {...field}
                        onChange={(e) => {
                          field.onChange(e);
                          onEmailChange?.(e.target.value);
                        }}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="password"
                render={({ field }) => (
                  <FormItem className="space-y-1">
                    <div className="flex-between">
                      <FormLabel>Password</FormLabel>
                      <Link
                        href={
                          form.watch("email")
                            ? `/forgot-password?email=${encodeURIComponent(form.watch("email"))}`
                            : "/forgot-password"
                        }
                        className="text-sm font-medium text-muted-foreground hover:opacity-75"
                      >
                        Forgot Password?
                      </Link>
                    </div>
                    <FormControl>
                      <PasswordInput placeholder="********" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <Button className="mt-2 bg-[#022f5d] hover:bg-[#02305dcc] text-white" loading={isLoading}>
                Login
              </Button>
            </div>
          </form>
        </Form>
      )}
    </div>
  );
}